/*
 * Copyright (c) Nexign, JSC, 2021.
 */
/**
 * CampaignInfo Page component
 *
 * @author Yakov Gushchin
 * @version 1.15.0
 * @since 1.12.0
 */

import { Fragment, useCallback, useEffect, useMemo } from 'react';

import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import Title from '../../../components/common/Title/Title';
import withAsyncStateManagement from '../../../modules/asyncStateManagment/WithAsyncStateManagement';
import PageLocale from '../../../modules/internationalization/components/PageLocale';
import { TranslationFileNamesEnum } from '../../../modules/internationalization/constants';
import {
    makeSelectPageCampaignInfoTranslation,
    makeSelectTranslationCommon,
} from '../../../modules/internationalization/intSelectors';
import { ICommonTranslation } from '../../../modules/internationalization/types';
import { ECommonTranslationPages } from '../../../modules/internationalization/types/common/pages';
import { IPageCampaignInfoTranslation } from '../../../modules/internationalization/types/pages/campaignInfo';
import { Loader } from '../../../modules/loader/components/Loader';
import { IRootReducer } from '../../../reducers/rootReducer';
import { IRouteParams } from '../../../routes/constants/routesConstants';
import { icons as themeIcons } from '../../../theme';
import ErrorPage from '../../ErrorPage/ErrorPage';
import { campaignInfoInitStartAction } from '../campaignInfoActions';
import campaignInfoReducer, { ICampaignInfoReducer } from '../campaignInfoReducer';
import { campaignInfoSagas } from '../campaignInfoSaga';
import {
    makeSelectCampaignInfoActions,
    makeSelectCampaignInfoGeneral,
    makeSelectCampaignInfoIsError,
    makeSelectCampaignInfoIsLoading,
} from '../campaignInfoSelectors';
import { TCampaignInfoInitStartPayload } from '../types/campaignInfoActionTypes';
import { ECampaignInfoActions } from '../types/campaignInfoTypes';
import CampaignInfoCard from './CampaignInfoCard';
import { CampaignInfoStyled } from './styled/CampaignInfoStyled';
import CampaignInfoTable from './Table/CampaignInfoTable';

export type TCampaignInfoPage = IConnectedProps & IDispatchProps;

const CampaignInfoPage = (props: TCampaignInfoPage) => {

    const {
        actions,
        info,
        isError,
        isLoading,
        translationCampaignInfo,
        translationCommon,

        init,
    } = props;

    const { campaignId } = useParams<IRouteParams>();

    useEffect(() => {
        init({ campaignId });
    }, [campaignId, init]);

    const isPageLoading = useCallback(() => isLoading || (info.id.toString() !== campaignId && !isError),
        [info.id, isError, isLoading, campaignId]);

    const isTableVisible = useMemo(() =>
        actions.includes(ECampaignInfoActions.participantList), [actions]);

    return (
        <Fragment>
            <PageLocale lazy
                        fileNames={[
                            TranslationFileNamesEnum.common,
                            TranslationFileNamesEnum.corporate,
                            TranslationFileNamesEnum.pageCampaignInfo,
                        ]}/>

            <Title pageName={ECommonTranslationPages.campaignInfo}/>

            <CampaignInfoStyled isTableVisible={isTableVisible}>
                <Loader showLoader={isPageLoading()}>
                    {
                        isError
                            ? <ErrorPage
                                src={themeIcons.errorDeliveryImage3x}
                                text={translationCommon.error.ui.text}
                                title={translationCommon.error.ui.title}
                            />
                            : <div className="campaign-info__content">
                                <CampaignInfoCard
                                    translationCampaignInfo={translationCampaignInfo}
                                />
                                {
                                    !isPageLoading() && isTableVisible && (
                                        <CampaignInfoTable
                                            campaignId={info.id.toString()}
                                            translationCampaignInfo={translationCampaignInfo}
                                            translationCommon={translationCommon}
                                            type={info.type}
                                        />
                                    )
                                }
                            </div>
                    }
                </Loader>
            </CampaignInfoStyled>
        </Fragment>
    );
};

export interface IConnectedProps extends Pick<ICampaignInfoReducer, 'actions' | 'info' | 'isLoading' | 'isError'> {
    translationCampaignInfo: IPageCampaignInfoTranslation;
    translationCommon: ICommonTranslation;
}

export const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    actions: makeSelectCampaignInfoActions(),
    info: makeSelectCampaignInfoGeneral(),
    isError: makeSelectCampaignInfoIsError(),
    isLoading: makeSelectCampaignInfoIsLoading(),
    translationCampaignInfo: makeSelectPageCampaignInfoTranslation(),
    translationCommon: makeSelectTranslationCommon(),
});

export interface IDispatchProps {
    init: (payload: TCampaignInfoInitStartPayload) => void;
}

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    init: payload => dispatch(campaignInfoInitStartAction(payload)),
});

export default withAsyncStateManagement(
    connect(mapStateToProps, mapDispatchToProps)(CampaignInfoPage),
    { sagas: campaignInfoSagas, reducer: { campaignInfo: campaignInfoReducer } },
);
